import React, { useState } from "react";
import Table, { Data, Row } from "../../components/ui/table/table";

import { useQuery } from "@tanstack/react-query";

import PageWrapper from "../../layouts/PageWrapper";
import Section from "../../layouts/Section";
import { getAmountWithCurrency, getFormattedDate } from "../../utils/utils";

import { Input, Select } from "../../components/form/formControls";
import { StatusBadge } from "../../components/StatusBadge";

import SubUserSelector from "./SubUserSelector";

import { FileText } from "react-feather";
import { useSelector } from "react-redux";
import { WALLET_API_URL } from "../../api/urlMetaData";
import WalletService, { fetchDriverPaymentReq } from "../../api/wallet-service";
import Pagination from "../../components/Pagination";
import { Button } from "../../components/ui/buttons/Button";
import { useDebounce } from "../../hooks/useDebounce";
import { usePagination } from "../../hooks/usePagination";
import { downloadReportDriverPaymentRequest } from "../../utils/downloadReport";
import { ViewRequestDocDrawer } from "./ViewRequestDocumentDrawer";

import { useLocale } from "../../hooks/useLocale";

const downloadReport = async ({ userHashId, dateRange, username }) => {
  try {
    const url = `${WALLET_API_URL.DOWNLOAD_DPREQ_REPORT}?dateRange=${dateRange}&username=${username}&userHashId=${userHashId}`;
    const res = await WalletService.get(url);
    return res?.data();
  } catch (error) {}
};

const ReportsCardLoad = () => {
  // globals
  const inputDate = useSelector((s) => s.global.inputDate);
  const currentUser = useSelector((s) => s.auth.currentUser);

  // user selected from the sub user list
  const selectedUser = useSelector((s) => s.reports.selectedUser);
  const { page } = usePagination();

  // Current Selcted Item
  const [currentRecord, setCurrentRecord] = useState(null);
  const [isDocumentDrawerOpen, setIsDocumentDrawerOpen] = useState(false);
  const [isDownoadingReport, setIsDownoadingReport] = useState(false);

  const { localize } = useLocale(["reports"]);

  // Handling Filters
  const filterSchema = {
    status: "All",
    driverName: "",
    mobile: "",
    moxeyId: "",
    requestAmount: "",
    referenceNumber: "",
  };
  const [filters, setFilters] = useState(filterSchema);
  const handleFilter = (filteredValues) => setFilters(filteredValues);
  const debouncedHandleFilters = useDebounce(handleFilter);

  const reportQuery = useQuery(
    ["reports/driverReqs", filters, inputDate, page, selectedUser?.userHashId],
    () =>
      fetchDriverPaymentReq({
        userHashId: selectedUser?.userHashId,
        userType: selectedUser?.productType || currentUser?.productType,
        dateRange: inputDate.date,
        filters,
        page,
      }),
    {
      enabled: !!selectedUser,
    }
  );

  // download report
  const handleDownloadReport = () => {
    setIsDownoadingReport(true);
    downloadReportDriverPaymentRequest({
      username: currentUser.username,
      email: currentUser.email,
      userHashId: selectedUser.userHashId,
      dateRange: inputDate.date,
    })
      .then((res) => {
        setIsDownoadingReport(false);
      })
      .catch((err) => {});
  };

  return (
    <>
      <PageWrapper>
        {/* Page Header */}
        <Section>
          <div className="d-flex">
            <h3>{localize("heading")}</h3>
            <div className="ms-auto">
              <SubUserSelector />
            </div>
          </div>
        </Section>

        {/* Table Data */}
        <Section>
          <>
            <div className="d-flex justify-content-between align-items-center mt-4">
              <h5 className="text-muted">
                {localize("driverPaymentRequest.heading")}
              </h5>

              <Button
                text={localize("driverPaymentRequest.actions.downloadReport")}
                loading={isDownoadingReport}
                className="btn btn-primary"
                onClick={handleDownloadReport}
              />
            </div>

            {/* Table Data */}
            <Table
              loading={reportQuery.isLoading}
              cols={localize("driverPaymentRequest.table.cols", {
                returnObjects: true,
              })}
              dataSize={reportQuery?.data?.content.length}
              filters={
                <TableFilters
                  filterSchema={filters}
                  handleFilter={debouncedHandleFilters}
                />
              }
            >
              {/* =============== Display Data from API =============*/}
              {reportQuery?.data?.content.map((item) => (
                <>
                  <Row>
                    {/* Status */}
                    <Data>
                      <StatusBadge status={item.status} />
                    </Data>

                    <Data>
                      {item.createdAt ? getFormattedDate(item.createdAt) : "-"}
                    </Data>
                    <Data>
                      {item.approveRejectDate
                        ? getFormattedDate(item.approveRejectDate)
                        : "-"}
                    </Data>
                    <Data>{item.clientName ? item.clientName : "-"}</Data>
                    <Data>
                      <span className="text-capitalize">
                        {item.expenseCategory ? item.expenseCategory : "-"}
                      </span>
                    </Data>
                    <Data>
                      {item.firstName
                        ? `${item.firstName} ${item.lastName}`
                        : "-"}
                    </Data>
                    <Data>
                      {item.toUserRole === "Broker" ? "PARTNER" : "DRIVER"}
                    </Data>
                    <Data>
                      {item.customerMobile ? item.customerMobile : "-"}
                    </Data>
                    <Data>{item.moxeyId ? item.moxeyId : "-"}</Data>
                    <Data>
                      {item.amount
                        ? getAmountWithCurrency(item.amount, item.currency)
                        : "-"}
                    </Data>
                    <Data>
                      {item.approvedAmount
                        ? getAmountWithCurrency(
                            item.approvedAmount,
                            item.currency
                          )
                        : "-"}
                    </Data>
                    <Data>{item.referenceNo ? item.referenceNo : "-"}</Data>
                    <Data>
                      {item.customerComment ? item.customerComment : "-"}
                    </Data>
                    <Data>
                      {item.approverComment ? item.approverComment : "-"}
                    </Data>
                    <Data>
                      {item.paymentReceiptsFileName ? (
                        <a
                          className="btn btn-light"
                          onClick={() => {
                            setCurrentRecord(item);
                            setIsDocumentDrawerOpen(true);
                          }}
                        >
                          <FileText size={12} />
                          <span className="ms-1 text-capitalize">
                            {localize(
                              "driverPaymentRequest.actions.viewDocuments"
                            )}
                          </span>
                        </a>
                      ) : (
                        <a className="btn btn-light disabled" disabled>
                          <FileText size={12} />
                          <span className="ms-1 text-capitalize">
                            {localize(
                              "driverPaymentRequest.actions.viewDocuments"
                            )}
                          </span>
                        </a>
                      )}
                    </Data>
                  </Row>
                </>
              ))}
              {/* show empty list */}
            </Table>

            {/* ======= Pagination =======*/}
            <div className="d-flex justify-content-end mb-5">
              <Pagination
                show={reportQuery.data?.content.length}
                totalPages={reportQuery.data?.totalPages}
                handlePageSelect={(page) =>
                  setFilters({ ...filters, page: page })
                }
              />
            </div>
          </>
        </Section>

        {/* View Documents Drawer */}
        {isDocumentDrawerOpen && (
          <ViewRequestDocDrawer
            title={localize("driverPaymentRequest.actions.viewDocuments")}
            data={currentRecord}
            onClose={() => {
              setIsDocumentDrawerOpen(false);
            }}
          />
        )}
      </PageWrapper>
    </>
  );
};

const TableFilters = ({ filterSchema, handleFilter }) => {
  {
    /**
     *
     * Note : "name" attribute is important for filter controls
     * Interpreted as : [name] and filterSchema.name
     *
     */
  }
  const statusOptions = [
    {
      value: "All",
      text: "All",
    },
    {
      value: "Requested",
      text: "Requested",
    },
    {
      value: "Rejected",
      text: "Rejected",
    },
    {
      value: "Approved",
      text: "Approved",
    },
    {
      value: "Pending",
      text: "Pending",
    },
    {
      value: "InProcess",
      text: "InProcess",
    },
  ];

  // table filter has its own state for persisting the filter values
  const [filteredValues, setFilteredValues] = useState(filterSchema);

  const handleFilteredValues = (event) => {
    const { name, value } = event.target;
    setFilteredValues({ ...filteredValues, [name]: value });
    handleFilter({ ...filteredValues, [name]: value, page: 0 });
  };

  return (
    <>
      <Row>
        <Data>
          <Select
            name="status"
            value={filteredValues.status}
            onChange={(event) => handleFilteredValues(event)}
          >
            {statusOptions.map((option) => (
              <option value={option.value}>{option.text}</option>
            ))}
          </Select>
        </Data>
        <Data />
        <Data />
        <Data />
        <Data />
        <Data>
          <Input
            name="driverName"
            value={filteredValues.driverName}
            onInput={(event) => handleFilteredValues(event)}
          />
        </Data>
        <Data />
        <Data>
          <Input
            name="mobile"
            value={filteredValues.mobile}
            onInput={(event) => handleFilteredValues(event)}
          />
        </Data>
        <Data>
          <Input
            name="moxeyId"
            value={filteredValues.moxeyId}
            onInput={(event) => handleFilteredValues(event)}
          />
        </Data>
        <Data>
          <Input
            name="requestAmount"
            value={filteredValues.requestAmount}
            onInput={(event) => handleFilteredValues(event)}
          />
        </Data>
        <Data />
        <Data>
          <Input
            name="referenceNumber"
            value={filteredValues.referenceNumber}
            onInput={(event) => handleFilteredValues(event)}
          />
        </Data>
        <Data />
        <Data />
        <Data />
      </Row>
    </>
  );
};

export default ReportsCardLoad;
